import { Input, TableColumnsType } from 'antd';
import usePagination from 'hooks/usePagination';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContentIcon from 'assets/icons/content';
import { useQuery } from 'react-query';
import { currencyApi } from 'services/apis';
import SearchIcon from 'assets/icons/search';
import CustomTable from 'components/ui/custom-table';
import type { CurrencyTableData } from './type';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';

export default function CurrencyTable() {
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const { t } = useTranslation();

  const columns: TableColumnsType<CurrencyTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index,
      },
      {
        key: 'name',
        title: t('currency.currencyName'),
        dataIndex: 'name',
        width: '20%',
        align: 'left',
      },
      {
        key: 'code',
        title: t('currency.currencyCode'),
        dataIndex: 'code',
        width: '10%',
        align: 'left',
      },
      {
        key: 'rate',
        title: t('currency.rate'),
        dataIndex: 'rate',
        width: '10%',
        align: 'left',
      },
      {
        key: 'year',
        title: t('currency.year'),
        dataIndex: 'year',
        width: '8%',
        align: 'left',
      },
      {
        key: 'effectiveFromDate',
        title: t('currency.effectiveDate'),
        dataIndex: 'effectiveFromDate',
        width: '15%',
        align: 'left',
      },
      {
        key: 'effectiveToDate',
        title: t('currency.upToDate'),
        dataIndex: 'effectiveToDate',
        width: '15%',
        align: 'left',
      },
    ],
    [t]
  );

  const {
    data: { items: currencyData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading,
  } = useQuery(['currencies', pageSize, currentPage, searchText], async () => {
    const params = {
      page: currentPage,
      pageSize,
      searchText: searchText,
    };
    const { items, pagination } = await currencyApi.filterCurrencies(params);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = items.slice(startIndex, endIndex);

    return {
      items: paginatedItems.map((c, index) => ({
        index: startIndex + index + 1,
        key: c.currencyCode,
        code: c.currencyCode,
        name: c.currencyName,
        year: c.effectiveFromDate?.getFullYear() || undefined,
        effectiveToDate: c.effectiveToDate,
        effectiveFromDate: c.effectiveFromDate,
        rate: Number(c.rate).toFixed(5),
      })),
      pagination: {
        ...pagination,
        total: items.length,
      },
    };
  });

  const handleSearchTrigger = () => setSearchText(inputText);

  return (
    <div className="w-full py-4">
      <div className="mt-6 mb-4 flex items-center gap-2">
        <Input
          placeholder={t('currency.searchCurrencyIfExists')}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={currencyData || []}
        total={total || 0}
        loading={isLoading}
      />
    </div>
  );
}
